import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Clock, LogIn, LogOut, ChevronRight, Calendar, X, Plus, Award,
  Trash2, Target,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useConsoleTheme } from '../../contexts/ThemeContext';
import { ThemeChoice } from '../../components/admin/ThemeChoice';
import { API } from '../../lib/api';
import { fmtINR, formatErr, notifyErr, inputCls, todayISO } from '../../components/admin/billing/shared';

const STAGE_BG = {
  present: 'bg-emerald-100 text-emerald-700',
  half_day: 'bg-amber-100 text-amber-700',
  paid_leave: 'bg-sky-100 text-sky-700',
  unpaid_leave: 'bg-slate-200 text-slate-700',
  absent: 'bg-red-100 text-red-700',
};

const StaffPortal = () => {
  const { user, authHeader, logout } = useAuth();
  const { logoUrl, tenant } = useTenant();
  useConsoleTheme();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [otOpen, setOtOpen] = useState(false);
  const [otForm, setOtForm] = useState({ date: todayISO(), hours: 1, invoice_ids: [], notes: '' });
  const [err, setErr] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [{ data: s }, { data: inv }] = await Promise.all([
        axios.get(`${API}/staff/me/summary`, { headers: authHeader() }),
        axios.get(`${API}/staff/me/recent-invoices`, { headers: authHeader() }),
      ]);
      setSummary(s);
      setRecentInvoices(inv || []);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (user && user.role !== 'staff') {
      navigate('/admin/inventory', { replace: true });
      return;
    }
    if (user) fetchAll();
  }, [user, fetchAll, navigate]);

  const clockIn = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/attendance/clock-in`, {}, { headers: authHeader() });
      await fetchAll();
    } catch (e) { notifyErr(e); }
    finally { setBusy(false); }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/attendance/clock-out`, {}, { headers: authHeader() });
      await fetchAll();
    } catch (e) { notifyErr(e); }
    finally { setBusy(false); }
  };

  const saveOT = async () => {
    if (!otForm.hours || otForm.hours <= 0) {
      setErr('OT hours must be greater than 0.');
      return;
    }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/overtime`, otForm, { headers: authHeader() });
      setOtOpen(false);
      setOtForm({ date: todayISO(), hours: 1, invoice_ids: [], notes: '' });
      await fetchAll();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const deleteOT = async (oid) => {
    if (!window.confirm('Delete this overtime entry?')) return;
    try {
      await axios.delete(`${API}/overtime/${oid}`, { headers: authHeader() });
      await fetchAll();
    } catch (e) { notifyErr(e); }
  };

  const toggleInvoice = (iid) => {
    setOtForm((f) => ({
      ...f,
      invoice_ids: f.invoice_ids.includes(iid) ? f.invoice_ids.filter((x) => x !== iid) : [...f.invoice_ids, iid],
    }));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-teal-600" size={28} /></div>;
  }
  if (!summary) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center">
      <div>
        <p className="text-red-600 mb-3">{err || 'Could not load your data.'}</p>
        <button onClick={logout} className="text-sm bg-slate-200 px-3 py-2 rounded">Log out</button>
      </div>
    </div>;
  }

  const att = summary.today_attendance;
  const mtd = summary.month_to_date;
  const staff = summary.staff;

  return (
    // `staff-scope` is the hook the accent theme overrides teal utilities on
    <div className="staff-scope min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-gradient-to-r from-teal-700 to-teal-600 text-white px-4 py-3 flex items-center justify-between" data-testid="staff-portal-header">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={logoUrl || '/brand/driveline-logo-white.png'}
            alt={logoUrl ? (tenant?.workshop_name || 'Workshop') : 'Driveline'}
            className={`w-auto hidden sm:block object-contain ${logoUrl ? 'max-h-8 max-w-[9rem] bg-white/90 rounded px-1.5 py-1' : 'h-5'}`}
            data-testid="staff-portal-brand"
          />
          <span className="h-7 w-px bg-white/25 hidden sm:block" />
          <div className="min-w-0">
            <div className="text-xs opacity-80">Welcome back</div>
            <div className="font-bold truncate">{staff.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ThemeChoice compact />
          <button
            onClick={() => navigate('/admin/my-performance')}
            className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded inline-flex items-center gap-1"
            data-testid="staff-my-performance-btn"
          >
            <Target size={14} /> My Performance
          </button>
          <button onClick={logout} className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded inline-flex items-center gap-1" data-testid="staff-logout-btn">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </header>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        {/* Today card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="text-teal-600" size={18} />
              <h2 className="font-semibold">Today · {todayISO()}</h2>
            </div>
            {att && <span className={`text-xs px-2 py-0.5 rounded-full ${STAGE_BG[att.status] || 'bg-slate-100'}`}>{att.status?.replace('_', ' ')}</span>}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm mb-3">
            <div>
              <div className="text-xs text-slate-500">Clock in</div>
              <div className="font-mono text-lg">{att?.clock_in || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Clock out</div>
              <div className="font-mono text-lg">{att?.clock_out || '—'}</div>
            </div>
          </div>
          <div className="flex gap-2">
            {!att?.clock_in ? (
              <button onClick={clockIn} disabled={busy} className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-semibold py-3 rounded-lg inline-flex items-center justify-center gap-2" data-testid="clock-in-btn">
                <LogIn size={16} /> Clock In
              </button>
            ) : !att?.clock_out ? (
              <button onClick={clockOut} disabled={busy} className="flex-1 bg-rose-500 hover:bg-rose-600 disabled:bg-rose-300 text-white font-semibold py-3 rounded-lg inline-flex items-center justify-center gap-2" data-testid="clock-out-btn">
                <LogOut size={16} /> Clock Out · {att.hours_worked ? `${att.hours_worked} hr` : 'Live'}
              </button>
            ) : (
              <div className="flex-1 text-center text-sm text-slate-500 py-3 bg-slate-50 rounded-lg">
                Day complete · <b>{att.hours_worked} hours</b>
              </div>
            )}
          </div>
        </div>

        {/* Month-to-date */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Award size={18} className="text-teal-600" /> Month-to-date</h2>
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[11px] text-slate-500">Days worked</div>
              <div className="font-bold text-lg">{mtd.paid_days}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[11px] text-slate-500">OT hours</div>
              <div className="font-bold text-lg">{mtd.ot_hours}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[11px] text-slate-500">Vehicles</div>
              <div className="font-bold text-lg">{mtd.incentive_lines.length}</div>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3 grid grid-cols-3 gap-2 text-xs">
            <div className="text-center">
              <div className="text-slate-500">Base accrued</div>
              <div className="font-mono font-bold text-slate-900">{fmtINR(mtd.base_due)}</div>
            </div>
            <div className="text-center">
              <div className="text-slate-500">OT pay</div>
              <div className="font-mono font-bold text-amber-700">{fmtINR(mtd.ot_pay)}</div>
            </div>
            <div className="text-center">
              <div className="text-slate-500">Incentive</div>
              <div className="font-mono font-bold text-emerald-700">{fmtINR(mtd.incentive_total)}</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 text-center">
            <div className="text-xs text-slate-500">Estimated net payable</div>
            <div className="text-2xl font-bold text-teal-700 font-mono">{fmtINR(mtd.net_payable)}</div>
          </div>
        </div>

        {/* Overtime log */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2"><Calendar size={18} className="text-teal-600" /> My Overtime</h2>
            <button onClick={() => setOtOpen(true)} className="text-xs bg-teal-500 hover:bg-teal-600 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="add-ot-btn">
              <Plus size={14} /> Add OT
            </button>
          </div>
          {summary.month_overtime.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No overtime entries this month.</p>
          ) : (
            <div className="space-y-2">
              {summary.month_overtime.map((o) => (
                <div key={o.id} className="bg-slate-50 rounded-lg p-2.5 flex items-center justify-between" data-testid={`ot-row-${o.id}`}>
                  <div className="text-sm">
                    <div className="font-medium">{o.date} · {o.hours} hrs</div>
                    <div className="text-[11px] text-slate-500">{o.invoice_ids.length} vehicle(s) {o.notes ? `· ${o.notes}` : ''}</div>
                  </div>
                  <button onClick={() => deleteOT(o.id)} className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Incentive vehicles */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><ChevronRight size={18} className="text-teal-600" /> Vehicles credited this month</h2>
          {mtd.incentive_lines.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-3">No vehicles assigned to you yet. Your manager assigns you to an invoice via "Worked on by".</p>
          ) : (
            <div className="space-y-1.5">
              {mtd.incentive_lines.map((inv) => (
                <div key={inv.invoice_id} className="flex justify-between text-sm py-1 border-b border-slate-50 last:border-b-0">
                  <span><span className="font-mono text-slate-600">{inv.invoice_number}</span> <span className="text-xs text-slate-400">· {inv.date}</span></span>
                  <span className="font-mono font-bold text-emerald-700">{fmtINR(inv.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* OT modal */}
      {otOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="text-sm font-semibold">Log Overtime</h2>
              <button onClick={() => setOtOpen(false)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded p-2 text-sm">{err}</div>}
              <div>
                <label className="block text-xs text-slate-500 mb-1">Date</label>
                <input type="date" value={otForm.date} onChange={(e) => setOtForm({ ...otForm, date: e.target.value })} className={inputCls} data-testid="ot-date" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">OT hours worked</label>
                <input type="number" min="0.25" step="0.25" value={otForm.hours} onChange={(e) => setOtForm({ ...otForm, hours: Number(e.target.value || 0) })} className={inputCls} data-testid="ot-hours" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Vehicles worked on (last 14 days)</label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {recentInvoices.length === 0 ? (
                    <div className="p-3 text-xs text-slate-400">No recent invoices.</div>
                  ) : recentInvoices.map((inv) => (
                    <label key={inv.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={otForm.invoice_ids.includes(inv.id)} onChange={() => toggleInvoice(inv.id)} data-testid={`ot-inv-${inv.id}`} />
                      <div className="flex-1 text-xs">
                        <div className="font-mono">{inv.number} · {inv.date?.slice(0, 10)}</div>
                        <div className="text-slate-500 truncate">{inv.subject || inv.customer_snapshot?.name || '—'}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Notes</label>
                <textarea rows={2} value={otForm.notes} onChange={(e) => setOtForm({ ...otForm, notes: e.target.value })} className={inputCls} placeholder="What you worked on…" />
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setOtOpen(false)} className="text-sm px-3 py-2 rounded text-slate-700 hover:bg-slate-200">Cancel</button>
              <button onClick={saveOT} disabled={busy} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded inline-flex items-center gap-1.5" data-testid="ot-save-btn">
                {busy && <Loader2 className="animate-spin" size={14} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPortal;
